import {useState, useRef, useEffect} from 'react';
import * as yup from 'yup';
import {Formik, Form, FieldArray} from 'formik';
import {useTranslation} from 'react-i18next';
import {useHistory} from 'react-router-dom';
import * as dayjs from 'dayjs';
import cloneDeep from 'lodash.clonedeep';
import { constants } from '../constants/constants';
import deleteCheckRowIcon from '../../images/new-dlt.png';
import {StyledChequeForm} from './paymentFlow.styled';
import GeneralPopUp from '../simple/popUpFunctional';
import BllinkUtils from '../utils/utils';
import {GAEvent} from '../google_analytics/google_analytics';
import LastConfirmationPageLogic from '../../tenants/tenant_payments/lastConfirmationPageLogic';
import {useDispatch, useSelector} from 'react-redux';
import ServerData from '../serverData';
import {hideLoader, showLoader} from '../../store/common/slice';
import {triggerRewardsNotification} from '../../managers/rewards/rewardsEvent';
import {Currency} from "../currency";

const BllinkMoney = BllinkUtils.Money;
const JsExtras = BllinkUtils.JsExtras;
const StringUtils = BllinkUtils.Strings;

const formats = ["DD.MM.YY", "DD.MM.YYYY", "DD/MM/YYYY", "DD-MM-YYYY"];

function parseDateString(value, originalValue) {
    return dayjs(originalValue, formats, true).isValid()
        ? dayjs(originalValue, formats).toDate()
        : new Date('');
}

export default function ChequeDepositForm({amount, buildingID, apartmentNumber}) {
    const {t} = useTranslation();
    const dispatch = useDispatch();
    const history = useHistory();
    const form = useRef();

    const [showAlertPopup, setShowAlertPopup] = useState(false);
    const [popupText, setPopupText] = useState({
        title: t('payments.offline_payment_amounts_are_wrong'),
        text: ''
    });
    const accessToken = useSelector((state) => state.auth.accessToken);
    let client = new ServerData(null, null, {accessToken: accessToken});

    useEffect(() => {
        if (form.current) {
            form.current[0].focus();
        }
    }, [form]);

    const initialFormValues = {
        cheques: [{
            branch: '',
            accountNumber: '',
            bankCode: '',
            chequeNumber: '',
            date: '',
            amount: ''
        }]
    };
    const validationSchema = yup.object().shape({
        cheques: yup.array()
            .of(
                yup.object().shape({
                    branch: yup.string().matches(/^\d+$/, t('titles.forms.number_field')),
                    accountNumber: yup.string(),
                    bankCode: yup.string(),
                    chequeNumber: yup.number(),
                    date: yup.date().transform(parseDateString),
                    amount: yup.string().matches(/^-?\d+\.?\d*$/, t('titles.forms.number_field')),
                })
            )
    });

    const addFields = (formikPush, values) => {
        const row = {
            bankCode: '',
            accountNumber: '',
            branch: '',
            amount: '',
            chequeNumber: '',
            date: ''
        };
        formikPush(row);
    };

    const validateTotalAmount = (values) => {
        const totalPaymentsSent = BllinkMoney.roundNumber(JsExtras.sum(values, 'amount') || 0, 2);
        const totalPaymentsShouldHaveBeenSent = BllinkMoney.roundNumber(amount.value, 2);

        if (totalPaymentsSent !== totalPaymentsShouldHaveBeenSent) {
            setPopupText({
                ...popupText,
                text: t('payments.offline_payment_amounts_are_wrong_text',
                    {
                        amountsSent: StringUtils.numberWithCommas(totalPaymentsSent),
                        totalToPay: StringUtils.numberWithCommas(totalPaymentsShouldHaveBeenSent)
                    })
            });

            GAEvent(constants.GoogleAnalytics.Events.popUps, 'popUpRender', 1, 'amountsSentOfflinePaymentsAreWrong');
            setShowAlertPopup(true);
            return false;
        }

        return true;
    };

    const buildPayload = (values) => {
        const cheques = cloneDeep(values.cheques);
        const payingFor = new LastConfirmationPageLogic({
            buildingID,
            apartmentNumber,
            paymentMethod: constants.managerPaymentsOptions.autDepositChequeServerKey
        }).buildingPaymentPayload({ignoreMissingInstallments: true});

        const isLastRowEmpty = Object.values(cheques[cheques.length - 1]).every(x => x === '');
        if (isLastRowEmpty) cheques.splice(cheques.length - 1);

        const a = dayjs(cheques[0].date, formats);

        // very risky. depends on the local cheque scanner
        const chequeScannerRightFormat = formats[2];

        const onlineCheques = cheques.map(cheque => ({
            amount: {
                value: parseFloat(cheque.amount),
                currency: amount.currency
            },
            paymentMethod: constants.managerPaymentsOptions.autDepositChequeServerKey,
            dueDate: dayjs(cheque.date, chequeScannerRightFormat).format('YYYY-MM-DDT00:00:00.000') + 'Z',
            extraDetails: {
                chequeID: cheque.chequeNumber,
                accountNumber: cheque.accountNumber,
                bankCode: cheque.bankCode,
                branchNumber: cheque.branch
            }
        }))

        return {
            paying_for: payingFor,
            tenant_details: payingFor.tenant_details,
            buildingID,
            apartmentNumber,
            totalToPay: amount.value,
            onlineCheques
        }
    };

    const createPaymentPlan = (values) => {
        const payload = buildPayload(values);
        if (buildingID == null || apartmentNumber == null) {
            console.error('[ChequeDepositForm] incorrect API call, path parameters are undefined');
        }
        return client.post(`/managers/external-payments/${buildingID}/${apartmentNumber}`, payload);
    }

    const submitForm = async (evt, values) => {
        evt.preventDefault();
        dispatch(showLoader());

        const isAmountValid = validateTotalAmount(values.cheques);

        if (!isAmountValid) {
            dispatch(hideLoader());
            return;
        }

        const result = await createPaymentPlan(values);

        GAEvent(constants.GoogleAnalytics.Events.offlinePayments,
            'successAddPayment',
            amount.value,
            constants.managerPaymentsOptions.chequeServerKey);

        dispatch(hideLoader());

        const cartID = result.cartID;
        if (cartID) {
            triggerRewardsNotification();
            history.push({pathname: `/${constants.pages.managerConfirmation}/${cartID}`});
        }
    }

    const createNewRow = (evt, amount, push, values, errors, touched, index, remove) => {
        if (evt.key === 'Enter' && remove) {
            remove(index);
            evt.stopPropagation();
            return;
        }
        if (evt.key === 'Tab') {
            const currentFormItemIndex = (index + 1) * 6 - 1;
            // form also contains submit button. focus next input if new cheque was added
            if (form.current.length - 2 === currentFormItemIndex && !errors) {
                addFields(push, values);
                form.current[currentFormItemIndex].focus();
            }
        }
    }

    const renderChequeRow = (index, {handleChange, handleBlur, remove, values, errors, touched, push}) => {
        const deleteIcon = index > 0 ? <img alt="delete-row" src={deleteCheckRowIcon}/> : '';

        return (
            <tbody key={index} >
                <tr>
                    <td scope="col">
                        <div className="form-group">
                            <label>{t('offline_payments.bank_code_title')}: </label>
                            <input
                                type="text"
                                className={`form-control  ${errors && touched && errors[index]?.bankCode && touched[index]?.bankCode ? 'error' : ''}`}
                                name={`cheques[${index}].bankCode`}
                                value={values[index].bankCode || ''}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                tabIndex={0}
                            />
                            {
                                errors && touched && errors[index]?.bankCode && touched[index]?.bankCode && (
                                    <label className={'error'}>{errors[index]?.bankCode}</label>
                                )
                            }
                        </div>
                    </td>
                    <td scope="col">
                        <div className="form-group">
                            <label
                                htmlFor={`cheques[${index}].branch`}>{t('offline_payments.bank_branch_title')}: </label>
                            <input
                                type="text"
                                className={`form-control  ${errors && touched && errors[index]?.branch && touched[index]?.branch ? 'error' : ''}`}
                                name={`cheques[${index}].branch`}
                                value={values[index].branch || ''}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                tabIndex={0}
                            />
                            {
                                errors && touched && errors[index]?.branch && touched[index]?.branch && (
                                    <label className={'error'}>{errors[index]?.branch}</label>
                                )
                            }
                        </div>
                    </td>
                    <td scope="col">
                        <div className="form-group">
                            <label
                                htmlFor={`cheques[${index}].accountNumber`}>{t('offline_payments.bank_account_number_title')}:</label>
                            <input
                                type="text"
                                className={`form-control ${errors && touched && errors[index]?.accountNumber && touched[index]?.accountNumber ? 'error' : ''}`}
                                name={`cheques[${index}].accountNumber`}
                                value={values[index].accountNumber || ''}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                tabIndex={0}
                            />
                            {
                                errors && touched && errors[index]?.accountNumber && touched[index]?.accountNumber && (
                                    <label className={'error'}>{errors[index]?.accountNumber}</label>
                                )
                            }
                        </div>
                    </td>
                    <td scope="col"/>
                </tr>
                <tr className="js-cheque-row">
                    <td scope="col">
                        <p/>
                        <div className="form-group">
                            <label htmlFor={`cheques[${index}].chequeNumber`}>{`${t('offline_payments.cheque_number')} ${index + 1} :*`}</label>
                            <input
                                type="text"
                                className={`form-control ${errors && touched && errors[index]?.chequeNumber && touched[index]?.chequeNumber ? 'error' : ''}`}
                                name={`cheques[${index}].chequeNumber`}
                                value={values[index].chequeNumber || ''}
                                onBlur={handleBlur}
                                onChange={handleChange}
                                tabIndex={0}
                            />
                            {
                                errors && touched && errors[index]?.chequeNumber && touched[index]?.chequeNumber && (
                                    <label className={'error'}>{errors[index]?.chequeNumber}</label>
                                )
                            }
                        </div>
                        <p/>
                    </td>
                    <td scope="col">
                        <p/>
                        <div className="form-group">
                            <label htmlFor={`cheques[${index}].date`}>{t('offline_payments.date')}:*</label>

                            <input
                                type="text"
                                className={`form-control  ${errors && touched && errors[index]?.date && touched[index]?.date ? 'error' : ''}`}
                                name={`cheques[${index}].date`}
                                value={values[index].date || ''}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                tabIndex={0}
                            />
                            {
                                errors && touched && errors[index]?.date && touched[index]?.date && (
                                    <label className={'error'}>{errors[index]?.date}</label>
                                )
                            }
                        </div>
                        <p/>
                    </td>
                    <td scope="col">
                        <p/>
                        <div className="form-group">
                            <label htmlFor={`cheques[${index}].amount`}>{t('offline_payments.amount')}:*</label>
                            <input
                                type="text"
                                className={`form-control ${errors && touched && errors[index]?.amount && touched[index]?.amount ? 'error' : ''}`}
                                name={`cheques[${index}].amount`}
                                value={values[index].amount || ''}
                                onBlur={handleBlur}
                                onChange={handleChange}
                                tabIndex={0}
                                onKeyDown={evt => createNewRow(evt, values[index].amount, push, values, errors, touched, index)}
                            />
                            {
                                errors && touched && errors[index]?.amount && touched[index]?.amount && (
                                    <label className={'error'}>{errors[index]?.amount}</label>
                                )
                            }
                        </div>
                        <p/>
                    </td>
                    {
                        index > 0 && (<td scope="col">
                            <p/>
                            <div
                                className="remove-cheque-n"
                                onClick={() => remove(index)}
                            >
                                {deleteIcon}
                            </div>
                            <p/>
                        </td>)
                    }
                </tr>
            </tbody>
        )
    }

    return (
        <StyledChequeForm className="col-md-12">
            <div className="modal-content n_content">
                <div className="modal-header">
                    <h5 className="col-12 modal-title text-center">
                        {t('offline_payments.pay_with_cheque_title')}
                        <p className={'mb-0'}>{t('offline_payments.auto_cheque_explanation1')}</p>
                        <p className={'mb-0'}>{t('offline_payments.auto_cheque_explanation2')}</p>
                        <p className={'mb-0'}>{t('offline_payments.auto_cheque_explanation3')}</p>
                        <p className={'mb-0'}>{t('offline_payments.auto_cheque_explanation4')}</p>
                        <p className={'mb-0'}>{t('offline_payments.auto_cheque_explanation5')}</p>
                        <p className={'mb-0'}>{t('offline_payments.auto_cheque_explanation6')}</p>
                    </h5>
                </div>
                <div className="modal-body">
                    <Formik
                        initialValues={initialFormValues}
                        validationSchema={validationSchema}
                    >
                        {({
                              errors,
                              touched,
                              values,
                              handleChange,
                              handleBlur,
                              isValid,
                              dirty,
                              setFieldValue,
                              setFieldTouched
                          }) => (
                            <Form className="offline-payment-body js-offline-payment-form js-cheque-form" ref={form}>
                                <FieldArray name="cheques">
                                    {({remove, push}) => (
                                        <>
                                            <table className="table cheque-table-form">
                                                {values.cheques.length > 0 &&
                                                    values.cheques.map((friend, index) => renderChequeRow(index, {
                                                        handleChange,
                                                        handleBlur,
                                                        remove,
                                                        setFieldValue,
                                                        setFieldTouched,
                                                        values: values.cheques,
                                                        errors: errors.cheques,
                                                        touched: touched.cheques,
                                                        push
                                                    }))
                                                }
                                            </table>
                                            <div className="row">
                                                <div className="col-sm-6">
                                                </div>
                                                <div className="col-sm-2">
                                                    <div className="form-group">
                                                        <label>{t('offline_payments.amount')}: </label>
                                                        <br/>
                                                        <label>{amount.value} <Currency>{amount.currency}</Currency></label>
                                                    </div>
                                                </div>

                                                <div className="col-sm-4">
                                                    <button
                                                        type="submit"
                                                        className="bllink-button"
                                                        disabled={!(isValid && dirty)}
                                                        onClick={(evt) => submitForm(evt, values)}
                                                    >
                                                        {t('offline_payments.make_offline_payment_button')}
                                                    </button>
                                                </div>
                                            </div>
                                        </>
                                    )}
                                </FieldArray>
                            </Form>
                        )}
                    </Formik>
                </div>
            </div>

            <GeneralPopUp
                show={showAlertPopup}
                onHideModal={() => setShowAlertPopup(false)}
                text={popupText.text}
                title={popupText.title}
            />
        </StyledChequeForm>
    )
}
